import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, ScrollArea, cn } from '@repo/ui'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

export type ContentSettingsKind = 'files' | 'text' | 'connectors'
export type ProfileSettingsSection = 'profile' | 'security'
export type OrganisationSettingsSection = 'account' | 'users' | 'content'
export type WorkspaceSettingsSection = 'general' | 'content' | 'danger'

export type SettingsTarget =
  | { scope: 'profile'; section: ProfileSettingsSection }
  | {
      scope: 'organisation'
      section: OrganisationSettingsSection
      contentKind?: ContentSettingsKind
      contentItemId?: number
    }
  | {
      scope: 'workspace'
      projectId: number
      section: WorkspaceSettingsSection
      contentKind?: ContentSettingsKind
      contentItemId?: number
    }

type ScopedSettingsContextValue = {
  target: SettingsTarget | undefined
  openSettings: (target: SettingsTarget) => void
  closeSettings: () => void
}

const ScopedSettingsContext = createContext<ScopedSettingsContextValue | undefined>(undefined)

export function ScopedSettingsProvider({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<SettingsTarget>()
  const targetRef = useRef<SettingsTarget | undefined>(undefined)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const openSettings = useCallback((nextTarget: SettingsTarget) => {
    if (targetRef.current === undefined && typeof document !== 'undefined') {
      const activeElement = document.activeElement
      if (activeElement instanceof HTMLElement && activeElement !== document.body) {
        returnFocusRef.current = activeElement
      }
    }
    targetRef.current = nextTarget
    setTarget(nextTarget)
  }, [])
  const closeSettings = useCallback(() => {
    targetRef.current = undefined
    setTarget(undefined)
    const returnFocus = returnFocusRef.current
    returnFocusRef.current = null
    if (returnFocus !== null) {
      window.setTimeout(() => {
        if (returnFocus.isConnected) returnFocus.focus()
      })
    }
  }, [])
  const value = useMemo<ScopedSettingsContextValue>(
    () => ({
      target,
      openSettings,
      closeSettings
    }),
    [closeSettings, openSettings, target]
  )

  return <ScopedSettingsContext.Provider value={value}>{children}</ScopedSettingsContext.Provider>
}

export function useScopedSettings(): ScopedSettingsContextValue {
  const context = useContext(ScopedSettingsContext)
  if (context === undefined) throw new Error('Scoped settings are unavailable.')
  return context
}

export type SettingsDialogSection<TValue extends string> = {
  value: TValue
  label: string
  icon?: ReactNode
}

export function SettingsSection({
  id,
  title,
  summary,
  action,
  children
}: {
  id: string
  title: string
  summary?: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section aria-labelledby={id} className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b pb-3">
        <div className="min-w-0">
          <h2 id={id} className="text-base font-semibold">
            {title}
          </h2>
          {summary !== undefined && <p className="mt-0.5 text-xs text-muted-foreground">{summary}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  )
}

export function SettingsDialogFrame<TValue extends string>({
  open,
  title,
  description,
  monogram,
  sections,
  activeSection,
  onOpenChange,
  onSectionChange,
  children
}: {
  open: boolean
  title: string
  description: string
  monogram?: string
  sections: ReadonlyArray<SettingsDialogSection<TValue>>
  activeSection: TValue
  onOpenChange: (open: boolean) => void
  onSectionChange: (section: TValue) => void
  children: ReactNode
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="grid h-dvh w-screen max-w-none grid-rows-[auto_1fr] gap-0 overflow-hidden rounded-none p-0 sm:h-[min(90vh,48rem)] sm:max-w-5xl sm:rounded-xl">
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 pr-14 text-left sm:px-6">
          {monogram !== undefined && (
            <span
              aria-hidden="true"
              className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-accent text-sm font-semibold text-primary"
            >
              {monogram}
            </span>
          )}
          <div className="min-w-0">
            <DialogTitle className="truncate">{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </div>
        </DialogHeader>
        <div className="grid min-h-0 grid-rows-[auto_1fr] md:grid-cols-[13rem_minmax(0,1fr)] md:grid-rows-1">
          <nav
            aria-label="Settings sections"
            className="flex gap-1 overflow-x-auto border-b bg-muted/25 p-2 md:flex-col md:overflow-x-visible md:border-r md:border-b-0 md:p-3"
          >
            {sections.map((section) => {
              const active = section.value === activeSection
              return (
                <Button
                  key={section.value}
                  type="button"
                  variant="ghost"
                  className={cn(
                    'justify-start px-3 text-muted-foreground',
                    active && 'bg-accent font-medium text-primary hover:bg-accent hover:text-primary'
                  )}
                  {...(active ? { 'aria-current': 'page' as const } : {})}
                  onClick={() => onSectionChange(section.value)}
                >
                  {section.icon}
                  {section.label}
                </Button>
              )
            })}
          </nav>
          <ScrollArea className="min-h-0">
            <div className="mx-auto w-full max-w-3xl p-5 sm:p-6 lg:p-8">{children}</div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}
